import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import Editor from '@monaco-editor/react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faSpinner, faCheckCircle, faExclamationTriangle, faSync } from "@fortawesome/free-solid-svg-icons";
import databaseApi from '~/api/databaseApi';

const TYPE_OPTIONS = [
    { value: 'StoredProcedures', label: 'Stored Procedures' },
    { value: 'Views', label: 'Views' },
    { value: 'Functions', label: 'Functions' }
];

function ViewScriptTab({ databases, customSelectStyles }) {
    const [selectedDb, setSelectedDb] = useState(null);
    const [selectedType, setSelectedType] = useState(TYPE_OPTIONS[0]);
    const [selectedObject, setSelectedObject] = useState(null);
    
    const [objectsData, setObjectsData] = useState({ StoredProcedures: [], Views: [], Functions: [] });
    const [loadingObjects, setLoadingObjects] = useState(false);
    
    const [scriptContent, setScriptContent] = useState('-- Select an object to view its script');
    const [loadingScript, setLoadingScript] = useState(false);
    const [executing, setExecuting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' }); // type: 'success' or 'error'

    // Fetch Objects when DB changes
    useEffect(() => {
        if (!selectedDb) {
            setObjectsData({ StoredProcedures: [], Views: [], Functions: [] });
            setSelectedObject(null);
            return;
        }
        fetchObjects(selectedDb.value);
    }, [selectedDb]);

    const fetchObjects = async (dbName) => {
        setLoadingObjects(true);
        setSelectedObject(null);
        setScriptContent('-- Select an object to view its script');
        setMessage({ type: '', text: '' });
        try {
            const res = await databaseApi.getDatabaseObjects(dbName);
            if (res.data?.Succeeded || res.data?.succeeded) {
                const data = res.data.Data || res.data.data || res.data;
                setObjectsData({
                    StoredProcedures: data.StoredProcedures || data.storedProcedures || [],
                    Views: data.Views || data.views || [],
                    Functions: data.Functions || data.functions || []
                });
            }
        } catch (error) {
            console.error("Failed to fetch database objects", error);
        }
        setLoadingObjects(false);
    };

    // Fetch Script when Object changes
    useEffect(() => {
        if (!selectedDb || !selectedObject) {
            setScriptContent('-- Select an object to view its script');
            return;
        }
        fetchScript(selectedDb.value, selectedObject.value);
    }, [selectedObject]);

    const fetchScript = async (dbName, objName) => {
        setLoadingScript(true);
        setMessage({ type: '', text: '' });
        try {
            // Using EXEC to switch context and retrieve the definition
            // We use sys.sql_modules to get the definition of the object
            const query = `USE [${dbName}]; SELECT sm.definition AS Script FROM sys.sql_modules AS sm JOIN sys.objects AS o ON sm.object_id = o.object_id WHERE o.name = '${objName}';`;
            const res = await databaseApi.executeQuery(query);
            
            if (res.data?.Succeeded || res.data?.succeeded) {
                const data = res.data.Data || res.data.data || res.data;
                const rows = data?.Rows || data?.rows || [];
                if (rows.length > 0 && rows[0].Script) {
                    setScriptContent(rows[0].Script);
                } else {
                    setScriptContent('-- Script not found or object is encrypted.');
                }
            } else {
                setScriptContent(`-- Failed to load script: ${res.data?.ErrorMessage || 'Unknown error'}`);
            }
        } catch (error) {
            console.error("Failed to fetch script", error);
            setScriptContent('-- Error loading script.');
        }
        setLoadingScript(false);
    };

    const handleExecute = async () => {
        if (!selectedDb || !scriptContent.trim()) return;
        
        setExecuting(true);
        setMessage({ type: '', text: '' });
        
        try {
            // We need to execute the user's script in the context of the selected database.
            // Since CREATE/ALTER must be the first statement in a batch, we wrap it in EXEC()
            const escapedScript = scriptContent.replace(/'/g, "''");
            const executeQuery = `USE [${selectedDb.value}]; EXEC('${escapedScript}');`;
            
            const res = await databaseApi.executeQuery(executeQuery);
            
            if (res.data?.Succeeded || res.data?.succeeded) {
                const data = res.data.Data || res.data.data || res.data;
                if (data?.ErrorMessage) {
                    setMessage({ type: 'error', text: data.ErrorMessage });
                } else {
                    setMessage({ type: 'success', text: 'Script executed successfully.' });
                }
            } else {
                setMessage({ type: 'error', text: res.data?.Message || res.data?.ErrorMessage || 'Execution failed.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.Message || error.message || 'Execution failed.' });
        }
        setExecuting(false);
    };

    const currentTypeObjects = objectsData[selectedType.value] || [];
    const objectOptions = currentTypeObjects.map(obj => ({ value: obj.Name || obj.name, label: obj.Name || obj.name }));

    return (
        <div className="pt-3 d-flex flex-column" style={{ height: '550px' }}>
            <div className="row g-3 mb-3">
                <div className="col-md-4">
                    <label className="form-label fs-9 fw-semibold mb-1">Database</label>
                    <Select
                        options={databases}
                        value={selectedDb}
                        onChange={setSelectedDb}
                        styles={customSelectStyles}
                        placeholder="Select Database..."
                        isClearable
                    />
                </div>
                <div className="col-md-3">
                    <label className="form-label fs-9 fw-semibold mb-1">Object Type</label>
                    <Select
                        options={TYPE_OPTIONS}
                        value={selectedType}
                        onChange={(option) => {
                            setSelectedType(option);
                            setSelectedObject(null);
                        }}
                        styles={customSelectStyles}
                    />
                </div>
                <div className="col-md-5">
                    <label className="form-label fs-9 fw-semibold mb-1">
                        Select {selectedType.label} {loadingObjects && <FontAwesomeIcon icon={faSpinner} spin className="ms-1 text-primary" />}
                    </label>
                    <Select
                        options={objectOptions}
                        value={selectedObject}
                        onChange={setSelectedObject}
                        styles={customSelectStyles}
                        placeholder={`Choose a ${selectedType.label.toLowerCase()}...`}
                        isDisabled={!selectedDb || loadingObjects}
                        isClearable
                    />
                </div>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} p-2 fs-9 mb-3 d-flex align-items-center`}>
                    <FontAwesomeIcon icon={message.type === 'success' ? faCheckCircle : faExclamationTriangle} className="me-2" />
                    <span style={{ whiteSpace: 'pre-wrap' }}>{message.text}</span>
                </div>
            )}

            <div className="flex-grow-1 border rounded d-flex flex-column overflow-hidden position-relative">
                {loadingScript && (
                    <div className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center bg-white" style={{ zIndex: 10, opacity: 0.7 }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                )}
                
                <div className="bg-light border-bottom p-2 d-flex justify-content-between align-items-center">
                    <span className="fs-9 fw-semibold text-muted">
                        {selectedObject ? `Script: ${selectedObject.value}` : 'Editor'}
                    </span>
                    <button 
                        className="btn btn-sm btn-primary d-flex align-items-center"
                        onClick={handleExecute}
                        disabled={executing || !selectedDb || !selectedObject || loadingScript}
                    >
                        {executing ? (
                            <><FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Executing...</>
                        ) : (
                            <><FontAwesomeIcon icon={faPlay} className="me-2" /> Execute Script</>
                        )}
                    </button>
                </div>

                <div className="flex-grow-1">
                    <Editor
                        height="100%"
                        defaultLanguage="sql"
                        language="sql"
                        theme="vs-light"
                        value={scriptContent}
                        onChange={(val) => setScriptContent(val || '')}
                        options={{
                            minimap: { enabled: false },
                            scrollBeyondLastLine: false,
                            fontSize: 14,
                            wordWrap: 'on',
                            automaticLayout: true
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

export default ViewScriptTab;
